  import { defineComponent, type PropType } from "vue";
  import DeliveryAmount from '@/extend/controls/delivery/DeliveryAmount';
  import DeliveryCount from '@/extend/controls/delivery/DeliveryCount';
  import DeliveryReference from '@/extend/controls/delivery/DeliveryReference';
  import Delivery from '@/extend/classes/Delivery';
  import { FieldStatus, EditFieldTheme, SectionStatus } from '@/framework/base/components/FieldInfo';
  import { Alerts } from '@/framework/base/helpers/Alerts'; 
  import { storeDelivery } from '@/generated/stores';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import { Extended } from '@/framework/base/classes/Schema';
  import ButtonPlus from '@/framework/base/components/ButtonPlus.vue';
  import Route from '@/generated/classes/Route';
  import { Fiches, Sections } from '@/generated/classes/AppSchema';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  export default defineComponent({
    components: {
      SectionHeader,
      LoaderButton,
      ButtonPlus,
      DeliveryAmount,
      DeliveryCount,
      DeliveryReference,
    },

    props: {
      delivery: {
        type: Object as PropType<Delivery>,
        required: true
      },
      showButton: {
        type: Boolean,
      },
      autoSave: {
        type: Boolean,
      },
      fieldOnly: {
        type: Boolean,
      },
      extended: {
        type: Boolean,
      },
      autoClear: {
        type: Boolean,
      },
      dialog: {
        type: Boolean,
      },
      closeSection: {
        type: Boolean,
      },
      showMissing: {
        type: Boolean,
      },
      moduleId: {
        type: Number,
      },
      theme: {
        type: Object as PropType<EditFieldTheme>,
      },
      fiche: {
        type: String,
      },
      fromSection: {
        type: String,
      },
      requiredAttributs: {
        type: Array as PropType<string[]>,
      },
    },

    beforeUnmount (): void {
      if (!this.closeSection) {
        this.beforeClose();
      }
    },

    data() {
      return {
        amountStatus: new FieldStatus(false, false, 0),
        strAmount: '',
        countStatus: new FieldStatus(false, false, 0),
        strCount: '',
        referenceStatus: new FieldStatus(false, false, 0),
        strReference: '',
        refDelivery: new Delivery,
        cloneDelivery: new Delivery,
      deliverySaving: false,
        saving: false,
        restoring: false,
        inDialog: false,
        updated: false,
        error: false,
        saveLoading: false,
        saveFailed: false,
        allowClearRef: true,
        objType: ObjTypes.DELIVERY,
        status: new SectionStatus(false, false, false),
        autoClose: true,
        closing: false
      }
    },
    computed: {
      isExtended (): boolean {
        return this.extended;
      },

      exist (): boolean {
        return this.editDelivery.id != 0
      },

      extendIcon (): string {
        return this.extended ? 'fas fa-circle-minus' : 'fas fa-circle-plus';
      },

      saveButton (): boolean {
        return (this.showButton == undefined || this.showButton == true) && !this.allReadOnly;
      },

      editDelivery: {
        get(): Delivery {
          if (this.closeSection) {
            this.beforeClose();
          } else {
            if (this.delivery != undefined) {
              if (this.delivery.id == 0) {
                Alerts.debug('CrudDelivery : les objets passés en paramètre doivent être issus du store (id <> 0)');
              }
              if (this.cloneDelivery.id != this.delivery.id && !this.saving) {
                this.handleDeliveryChange();
              }
            }
          }
          return this.refDelivery;
        },
        set(value: Delivery) {
          this.refDelivery = value;
        }
      },

      canCreate (): boolean {
        return this.refDelivery.id > 0;
      },

      canDelete (): boolean {
        return this.refDelivery.canDelete && this.refDelivery.id > 0;
      },

      updatedAndValid (): boolean {
        return this.updated && !this.error;
      },

      clearAuto (): boolean {
        return (this.autoClear == undefined || this.autoClear == true) && this.allowClearRef;
      },


      amount: {
        get(): string {
          return this.strAmount;
        },
        set(value: string) {
          const amount = parseFloat(value);
          if (amount.toString() == value) {
            this.editDelivery.amount = amount;
            this.fieldUpdated('AMOUNT', value);
          }
          this.strAmount = value;
        }
      },


      count: {
        get(): string {
          return this.strCount;
        },
        set(value: string) {
          const count = parseInt(value);
          if (count.toString() == value) {
            this.editDelivery.count = count;
            this.fieldUpdated('COUNT', value);
          }
          this.strCount = value;
        }
      },


      reference: {
        get(): string {
          return this.editDelivery.reference;
        },
        set(value: string) {
          this.editDelivery.reference = value;
          this.fieldUpdated('REFERENCE', value);
        }
      },


      allReadOnly (): boolean {
        const res = this.delivery.canUpdate && (
        (this.isDisplayed('AMOUNT') && !this.readOnly('AMOUNT')) ||
        (this.isDisplayed('COUNT') && !this.readOnly('COUNT')) ||
        (this.isDisplayed('REFERENCE') && !this.readOnly('REFERENCE')));
        return !res;
      },

      fields (): string[] {
        return ['AMOUNT', 'COUNT', 'REFERENCE'];
      },
    },

    methods: {
      handleDeliveryChange() : void {
        // Edited object changed
        this.saveFailed = false;
        if (this.autoSave && this.updatedAndValid) {
          if (!this.inDialog && this.checkFields()) {
            // Dialogue to save or cancel changes
            this.inDialog = true;
            this.dialogSaveOrCancel().then(() => {
              this.createRef(this.delivery);
              this.inDialog = false;
                  })
          } else if (!this.restoring && !this.inDialog) {
            this.restoreEdited();
            this.createRef(this.delivery);
          }
        } else if (!this.inDialog) {
          if (this.cloneDelivery.id < 0) {
            this.clearRef();
          } else if (this.updated) {
            this.restoreEdited();
          }
          this.createRef(this.delivery);
        }
      },
      beforeClose (): void {
        if (!this.closing) {
          this.closing = true;
          if (this.autoSave && this.updatedAndValid && this.checkFields()) {
            this.$emit('saveOrCancel');
            this.dialogSaveOrCancel();
          } else {
            if (this.autoSave && this.updated) {
              this.restoreEdited();
            }
            this.$emit('cancelled');
          }
          this.clearRef();
          }
      },

      setExtended (extended: Extended): void {
         this.$emit('extended', extended);
      },

      // eslint-disable-next-line
      isDisplayed (value: string): boolean {
        return true;
      },

      // eslint-disable-next-line
      required (value: string): boolean {
        return false;
      },

      // eslint-disable-next-line
      readOnly (value: string): boolean {
        return false;
      },

      save (): void {
        if (event != undefined) {
          event.stopPropagation();
        }
        this.saveLoading = true;
        this.saveFailed = false;
        if (this.checkFields()) {
          this.saveObj();
        } else {
        this.saveLoading = false;
          this.saveFailed = true;
        }
      },

      setUpdated (): void {
        const updated = (this.amountStatus != undefined && this.amountStatus.updated) ||
          (this.countStatus != undefined && this.countStatus.updated) ||
          (this.referenceStatus != undefined && this.referenceStatus.updated);
        if (updated != this.updated) {
          this.updated = updated;
          this.status.updated = updated;
          this.status.missing = this.hasMissing();
          if (!this.closing) {
            this.$emit('status', this.status);
          }
          this.$emit('updated', this.delivery);
        }
      },

      setError (): void {
        const error = this.amountStatus.error ||
        this.countStatus.error ||
        this.referenceStatus.error;
        if (error != this.error) {
          if (!error) {
            this.saveFailed = false;
          }
          this.error = error;
          this.status.error = error;
          if (!this.closing) {
            this.$emit('status', this.status);
          }
          this.$emit('updated', this.delivery);
        }
      },

      // Clear non saved new objects when leaving
      clearRef (): void {
        if (this.clearAuto && this.refDelivery.id < 0) {
          storeDelivery.mDeliveryDeleted(this.refDelivery.id);
        }
      },

      // Dialog to save or ignore changes when loosing focus or changing edited item
      dialogSaveOrCancel (): Promise<boolean> {
        return new Promise((resolve) => {
          if (!this.error) {
            Alerts.saveOrCancel()
              .then((result: boolean) => {
                if (result) {
                  this.saveObj();
                } else {
                  if (this.refDelivery.id <= 0) {
                    this.deleteObj();
                  } else {
                    this.restoreEdited();
                  }
                  this.$emit('cancelled');
                }
                resolve(result);
              })
          } else {
            this.restoreEdited();
          }
        })
      },

      // Save object and subobjects referenced by foreignClass keys
      saveObj (): void {
        this.saving = true;
        if (
          this.saveDelivery()
          ) {
          this.saving = false;
          this.saveLoading = false;
          this.deliverySaving = false;
          this.createRef(this.refDelivery);
          this.$emit('updated', this.refDelivery);
          if (this.delivery.id > 0) {
            this.$emit('saved', this.refDelivery);
          } else {
            this.updated = true;
          }
          if (this.autoClose) {
            this.saveFields();
            this.$emit('closeDialog');
          }
        } else {
          if (this.refDelivery.failed) {
          this.saveLoading = false;
            this.deliverySaving = false;
            this.refDelivery.failed = false;
          }
        }
      },

      saveDelivery (): boolean {
        if (!this.refDelivery.compareDelivery(this.cloneDelivery)) {
          if (!this.deliverySaving) {
            this.deliverySaving = true;
          if (this.refDelivery.id > 0) {
            storeDelivery.updateDelivery(this.refDelivery)
              .then((data: Delivery) => {
                if (!data.failed) {
                  this.refDelivery = storeDelivery.getDelivery(data.id);
                  this.cloneDelivery = data.cloneDelivery();
                } else {
                  this.refDelivery.failed = true;
                }
                this.saveObj();
              })
              .catch(() => {
                this.restoreEdited();
                this.deliverySaving = false;
                this.saveLoading = false;
              })
          }
            if (this.refDelivery.id <= 0) {
              storeDelivery.createDelivery(this.refDelivery)
                .then((data: Delivery) => {
                  if (!data.failed) {
                    this.refDelivery = storeDelivery.getDelivery(data.id);
                    this.cloneDelivery = data.cloneDelivery();
                  } else {
                this.refDelivery.failed = true;
                  }
                  this.saveObj();
                })
                .catch(() => {
                  this.restoreEdited();
                    this.deliverySaving = false;
                })
            }
          }
          return false;
        } else {
          return true;
        }
      },

      // Delete object
      deleteObj (): void {
        if (this.refDelivery.id <= 0) {
          storeDelivery.mDeliveryDeleted(this.refDelivery.id);
        }
      },

      statusAmount (value: FieldStatus): void {
        this.amountStatus = value;
        this.setUpdated();
        this.setError();
      },
      statusCount (value: FieldStatus): void {
        this.countStatus = value;
        this.setUpdated();
        this.setError();
      },
      statusReference (value: FieldStatus): void {
        this.referenceStatus = value;
        this.setUpdated();
        this.setError();
      },

      deleteDelivery(): void {
        this.$emit('deleteDelivery',  this.delivery);
      },

    addDelivery(): void {
      this.$emit('addDelivery',  this.delivery);
    },

      // Restore object
      restoreEdited (): void {
        if (this.cloneDelivery != undefined) {
          this.restoring = true;
          this.refDelivery.amount = this.cloneDelivery.amount;
          this.refDelivery.count = this.cloneDelivery.count;
          this.refDelivery.reference = this.cloneDelivery.reference;
          this.restoring = false;
        }
      },


    // Clone and clear edited
      createRef (value: Delivery): void {
        this.clearRef();
        this.editDelivery = value;
        this.cloneDelivery = value.cloneDelivery();
        this.amountStatus.clear(value.id);
        this.countStatus.clear(value.id);
        this.referenceStatus.clear(value.id);
        this.status.clear();
        this.strAmount = this.editDelivery.amount.toString();
        this.strCount = this.editDelivery.count.toString();
      },

    checkFields (): boolean {
        // @ts-expect-error : generated method
        if (this.isDisplayed('AMOUNT') && this.$refs.AMOUNT) this.$refs.AMOUNT.touch();
        // @ts-expect-error : generated method
        if (this.isDisplayed('COUNT') && this.$refs.COUNT) this.$refs.COUNT.touch();
        // @ts-expect-error : generated method
        if (this.isDisplayed('REFERENCE') && this.$refs.REFERENCE) this.$refs.REFERENCE.touch();
        return !this.error;
      },

      missing (value: string): boolean {
        return this.hasMissing(value);
      },

      hasMissing (value = ''): boolean {
        let missing = false;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'AMOUNT') && this.isDisplayed('AMOUNT') && this.$refs.AMOUNT && this.$refs.AMOUNT.missing()) missing = true;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'COUNT') && this.isDisplayed('COUNT') && this.$refs.COUNT && this.$refs.COUNT.missing()) missing = true;
        // @ts-expect-error : generated method
        if ((value == '' || value == 'REFERENCE') && this.isDisplayed('REFERENCE') && this.$refs.REFERENCE && this.$refs.REFERENCE.missing()) missing = true;
        return missing;
      },

      saveFields (): void {
        // @ts-expect-error : generated method
        if (this.isDisplayed('AMOUNT') && this.$refs.AMOUNT) this.$refs.AMOUNT.save();
        // @ts-expect-error : generated method
        if (this.isDisplayed('COUNT') && this.$refs.COUNT) this.$refs.COUNT.save();
        // @ts-expect-error : generated method
        if (this.isDisplayed('REFERENCE') && this.$refs.REFERENCE) this.$refs.REFERENCE.save();
      },

      getValue (field: string): string {
         if (field == 'AMOUNT') return this.amount.toString();
        else  if (field == 'COUNT') return this.count.toString();
        else  if (field == 'REFERENCE') return this.reference.toString();
        else return '';
      },

    // eslint-disable-next-line
    fieldUpdated (field: string, value: unknown): void {
      // To implement
    },

      setFocus (route: Route): boolean {
        let res = false;
        let first = '';
        const attribut = route ? route.attribut : '';
        this.fields.forEach(field => {
          // @ts-expect-error : generated method
          if (!res && this.isDisplayed(field) && !this.readOnly(field) && this.$refs[field].setFocus) {
            if (attribut == field) {
              // @ts-expect-error : generated method
              this.$refs[attribut].setFocus();
              res = true;
            } else if (first == '') {
              first = field;
            }
          }
        })
        // @ts-expect-error : generated method
        if (!res && first != '' && this.$refs[first] && this.$refs[first].setFocus) {
          // @ts-expect-error : generated method
          this.$refs[first].setFocus();
          res = true;
        }
        return res
      },

      closeDialog(): void {
        this.$emit('closeDialog')
      }
    },
  })
